import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiPhoneCall, FiArrowRight, FiCheckCircle, FiClock, FiGlobe, FiCamera } from "react-icons/fi";
import { FaWhatsapp, FaPrayingHands } from "react-icons/fa";
import { GiPrayerBeads, GiCrystalBall } from "react-icons/gi";
import { motion } from "framer-motion";

import SEO from "../components/SEO";
import SectionTitle from "../components/SectionTitle";
import PersonCard from "../components/PersonCard";
import ServiceCard from "../components/ServiceCard";
import TestimonialCarousel from "../components/TestimonialCarousel";
import StatsCounter from "../components/StatsCounter";
import FAQ from "../components/FAQ";
import FloatingDecor from "../components/FloatingDecor";
import HeroBookingCard from "../components/HeroBookingCard";
import Panchang from "../components/Panchang";
import YouTubeVideo from "../components/YouTubeVideo";
import Loader from "../components/Loader";
import OmSymbol from "../components/OmSymbol";

import api, { imgUrl } from "../utils/api";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { SAURABH, GAURI, SERVICE_AREAS, STATS, FAQS } from "../utils/siteData";
import { useServices } from "../hooks/useServices";

export default function Home() {
  const { poojaServices, astrologyServices, loading: loadingServices } = useServices("all");
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [featuredGallery, setFeaturedGallery] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api
      .get("/blogs/recent")
      .then((res) => setRecentBlogs(res.data))
      .catch(() => setRecentBlogs([]))
      .finally(() => setLoadingBlogs(false));

    api
      .get("/gallery/featured")
      .then((res) => setFeaturedGallery(res.data))
      .catch(() => setFeaturedGallery([]));

    api
      .get("/videos/featured")
      .then((res) => setVideos(res.data))
      .catch(() => setVideos([]));
  }, []);

  const heroWaLink = buildWhatsAppLink(
    SAURABH.whatsapp,
    "Namaste 🙏, I would like to book a pooja / astrology consultation."
  );

  return (
    <div>
      <SEO
        title="Pandit & Astrologer in Thane"
        description="Saurabh Shastri offers authentic Vedic pooja services and Gauri Shastri provides expert Astrology & Palmistry consultations in Thane, Mumbai and worldwide."
        keywords="pandit thane, pooja services thane, astrologer thane, palmist thane, vastu shanti thane, gruhpravesh thane"
        path="/"
      />

      {/* HERO */}
      <section className="relative bg-hero-gradient bg-mandala text-white overflow-hidden">
        {/* radial gold spotlight */}
        <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
        <FloatingDecor variant="mixed" />

        <div className="relative max-w-7xl mx-auto px-4 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow-chip bg-gold-500/15 border border-gold-300/40 text-gold-200 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-300 animate-twinkle" />
              Serving Thane &amp; Mumbai with Devotion
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-5">
              Authentic Poojas &amp; Trusted Astrology Guidance,{" "}
              <span className="text-gradient-gold">Rooted in Vedic Tradition</span>
            </h1>
            <div className="w-24 h-1 rounded-full bg-gradient-to-r from-gold-400 to-transparent mb-6" />
            <p className="text-gold-100/90 text-lg leading-relaxed mb-8 max-w-xl">
              Pandit <strong>Saurabh Shastri</strong> performs sacred Vedic poojas and sanskars with complete
              devotion, while <strong>Gauri Shastri</strong> offers accurate Astrology &amp; Palmistry
              guidance - serving families across Thane, Mumbai and worldwide.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={heroWaLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 px-6 py-3 rounded-full font-semibold shadow-premium-lg transition-colors"
              >
                <FaWhatsapp size={20} /> Book on WhatsApp
              </a>
              <a
                href={`tel:+91${SAURABH.phone}`}
                className="glass-card inline-flex items-center gap-2 text-white hover:text-gold-200 px-6 py-3 rounded-full font-semibold transition-colors"
              >
                <FiPhoneCall /> Call Now
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10">
              {["100% Authentic Vedic Rituals", "Trusted by Thane Families", "Worldwide Astrology Consultations"].map(
                (item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-gold-100/90">
                    <FiCheckCircle className="text-gold-300" /> {item}
                  </div>
                )
              )}
            </div>
          </motion.div>

          <HeroBookingCard />
        </div>
      </section>

      {/* STATS BAND */}
      <section className="relative -mt-12 lg:-mt-16 z-10">
        <div className="max-w-6xl mx-auto px-4">
          <StatsCounter stats={STATS} />
        </div>
      </section>

      {/* TODAY'S PANCHANG */}
      <section className="py-16 bg-saffron-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="Hindu Calendar"
            title="Today's Panchang"
            subtitle="Auspicious timings, tithi, nakshatra and more for today — plan your pooja and important activities accordingly."
          />
          <Panchang />
        </div>
      </section>

      {/* FEATURED PERSONS */}
      <section className="py-20 bg-saffron-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="Meet Our Experts"
            title="Devoted to Tradition, Trusted by Thousands"
            subtitle="Two pillars of guidance - sacred rituals and astrological wisdom - united to bring clarity, peace and blessings into your life."
          />

          <div className="space-y-20">
            <PersonCard
              person={SAURABH}
              role="Vedic Pandit & Pooja Specialist"
              ctaLink="/pooja-services"
              ctaLabel="View All Pooja Services"
            />
            <PersonCard
              person={GAURI}
              role="Astrologer & Palmist"
              ctaLink="/astrology-palmistry-services"
              ctaLabel="View Astrology Services"
              reverse
            />
          </div>
        </div>
      </section>

      {/* SAURABH SERVICES PREVIEW */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="Pooja Services by Saurabh Shastri"
            title="Sacred Rituals for Every Occasion"
            subtitle="From housewarming to weddings, each ceremony is performed with complete Vedic accuracy and heartfelt devotion."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {!loadingServices && poojaServices.slice(0, 8).map((s, i) => (
              <ServiceCard key={s.slug} title={s.title} desc={s.desc} image={s.image} detailLink={s.detailPath} index={i} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/pooja-services"
              className="btn-maroon inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
            >
              View All Pooja Services <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* GAURI SERVICES PREVIEW */}
      <section className="py-20 bg-maroon-50 bg-mandala">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <SectionTitle
            eyebrow="Astrology & Palmistry by Gauri Shastri"
            title="Accurate Guidance for Life's Big Questions"
            subtitle="Detailed horoscope readings, palmistry analysis and effective remedies for marriage, career, business and more."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {!loadingServices && astrologyServices.slice(0, 8).map((s, i) => (
              <ServiceCard key={s.slug} title={s.title} desc={s.desc} image={s.image} icon={<GiCrystalBall size={26} />} detailLink={s.detailPath} index={i} />
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/astrology-palmistry-services"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
            >
              View All Astrology Services <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle eyebrow="Why Choose Us" title="A Trusted Name in Thane for Pooja & Astrology" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <GiPrayerBeads size={28} />, title: "Authentic Vedic Process", desc: "Every ritual follows traditional Shastra-sanctioned procedures." },
              { icon: <FiClock size={26} />, title: "Punctual & Reliable", desc: "Timely arrival and well-organized ceremonies, every time." },
              { icon: <FiGlobe size={26} />, title: "Worldwide Astrology", desc: "Online consultations with Gauri Shastri available globally." },
              { icon: <FaPrayingHands size={26} />, title: "Complete Puja Material", desc: "Hassle-free arrangement of all ritual items, fully sorted for you." },
            ].map((f) => (
              <div key={f.title} className="text-center p-6 rounded-2xl border border-gold-100 hover-lift bg-white shadow-premium">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-saffron-100 to-gold-50 flex items-center justify-center text-saffron-600 shadow-inner">
                  {f.icon}
                </div>
                <h4 className="font-heading text-maroon-800 mb-2">{f.title}</h4>
                <p className="text-sm text-gray-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY HIGHLIGHTS */}
      {featuredGallery.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <SectionTitle
              eyebrow="Gallery"
              title="Glimpses of Our Ceremonies"
              subtitle="Moments of devotion captured during poojas, weddings and sacred rituals performed for families across Thane."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {featuredGallery.map((item) => (
                <Link
                  key={item._id}
                  to="/gallery"
                  className="group relative aspect-square rounded-2xl overflow-hidden shadow-premium border border-gold-100"
                >
                  {item.images && item.images[0] ? (
                    <img
                      src={imgUrl(item.images[0])}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gold-50 flex items-center justify-center text-3xl text-saffron-400">
                      <FiCamera size={32} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <p className="text-white text-xs font-medium line-clamp-2">{item.title}</p>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/gallery"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold"
              >
                View Full Gallery <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="py-20 bg-saffron-50">
        <div className="max-w-6xl mx-auto px-4">
          <SectionTitle eyebrow="Client Experiences" title="What Devotees Say About Us" />
          <TestimonialCarousel />
          <div className="text-center mt-6">
            <Link
              to="/testimonials"
              className="inline-flex items-center gap-2 text-maroon-700 font-semibold hover:gap-3 transition-all"
            >
              Read All Reviews &amp; Share Your Own <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* VIDEOS & REVIEWS */}
      {videos.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <SectionTitle
              eyebrow="Videos & Reviews"
              title="See Us in Action"
              subtitle="Product reviews, pooja ceremonies and client testimonials — watch and experience the devotion for yourself."
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((v, i) => (
                <motion.div
                  key={v._id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                  className="flex flex-col"
                >
                  <YouTubeVideo youtubeId={v.youtubeId} title={v.title} />
                  <div className="mt-3">
                    <span className="text-xs text-saffron-600 font-semibold uppercase tracking-wide">
                      {v.category}
                    </span>
                    <h4 className="font-heading text-maroon-800 text-base leading-tight mt-1 line-clamp-2">
                      {v.title}
                    </h4>
                    {v.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{v.description}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            eyebrow="Common Questions"
            title="Frequently Asked Questions"
            subtitle="Everything you need to know before booking a pooja or astrology consultation."
          />
          <FAQ items={FAQS} />
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-16 bg-saffron-50">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <SectionTitle eyebrow="Areas We Serve" title="Proudly Serving Thane & Mumbai Region" />
          <div className="flex flex-wrap justify-center gap-3">
            {SERVICE_AREAS.map((area) => (
              <span key={area} className="bg-white border border-gold-200 text-maroon-800 px-4 py-1.5 rounded-full text-sm shadow-sm hover:shadow-gold hover:border-gold-300 transition-all">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-20 bg-maroon-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle eyebrow="Knowledge Center" title="Latest from Our Blog" subtitle="Insights on astrology, vastu, pooja vidhi and Vedic traditions." />
          {loadingBlogs ? (
            <Loader />
          ) : recentBlogs.length === 0 ? (
            <p className="text-center text-gray-500">New articles coming soon. Please check back!</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentBlogs.map((b) => (
                <Link
                  key={b._id}
                  to={`/blog/${b.slug}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-premium hover-lift border border-gold-100 group"
                >
                  <div className="aspect-[16/10] bg-gold-100 overflow-hidden">
                    {b.coverImage ? (
                      <img src={imgUrl(b.coverImage)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-5xl text-saffron-400"><OmSymbol size={48} /></div>
                    )}
                  </div>
                  <div className="p-5">
                    <span className="text-xs text-saffron-600 font-semibold uppercase tracking-wide">{b.category}</span>
                    <h4 className="font-heading text-maroon-800 text-lg mt-1 mb-2 line-clamp-2">{b.title}</h4>
                    <p className="text-sm text-gray-600 line-clamp-2">{b.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <div className="text-center mt-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-maroon-700 font-semibold hover:gap-3 transition-all">
              Read More Articles <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20 bg-hero-gradient bg-mandala text-white text-center overflow-hidden">
        <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
        <FloatingDecor variant="om" />
        <div className="relative max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl sm:text-4xl mb-4">Ready to Begin Your Sacred Journey?</h2>
          <p className="text-gold-100/90 mb-8">
            Whether you need a pooja performed with devotion or seek clarity through astrology, we are here to guide you.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gold px-7 py-3 rounded-full font-semibold">
              Contact Us Today
            </Link>
            <a
              href={heroWaLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 px-7 py-3 rounded-full font-semibold shadow-premium-lg transition-colors"
            >
              <FaWhatsapp /> WhatsApp Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
